/**
 * Stable capability identifiers for the admin Device modal advanced / membership matrix.
 *
 * <p>These keys align with the columns documented in Confluence space {@code APM} page
 * {@code Device Modal} (page id {@code 4501209091}). UI fields and API properties should map
 * to these ids so visibility rules stay centralized.
 *
 * <p>Backend device type integers follow {@code CTelephonyModelConst.iDEVICE_TYPE_XXX} in
 * TelephonyModel; {@code Call Queue} (MS Teams) shares {@code iDEVICE_TYPE_GROUP_ACD} with
 * {@code Group (ACD)} per {@code CWSConstants.ConvertDeviceTypeStringToInt}.
 */

/** Confluence matrix column: advanced int dialling address. */
export const DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS = 'intDiallingAddress' as const

/** Confluence matrix column: advanced override monitoring. */
export const DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING = 'overrideMonitoring' as const

/**
 * Confluence matrix column: Broadworks ID / UPN / Webex Calling user id (integration-specific
 * presentation).
 */
export const DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID = 'broadworksIdOrUpnWebexUserId' as const

/** Confluence matrix column: MS Teams user id (Teams-only surfaces). */
export const DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID = 'msTeamsUserId' as const

/** Confluence matrix column: Cisco common identity (Webex Calling only). */
export const DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY = 'ciscoCommonIdentity' as const

/** BroadSoft M6 telephony monitor password field (M6 TelSys only; not a Confluence matrix column). */
export const DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD = 'telsysMonitorPassword' as const

/** Confluence matrix column: primary email address (Webex Calling only). */
export const DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS = 'primaryEmailAddress' as const

/** Confluence matrix column: disable real-time queue monitoring. */
export const DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING = 'disableRealtimeQueueMonitoring' as const

/** Confluence matrix column: owning group id. */
export const DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID = 'owningGroupId' as const

/** Confluence matrix column: owning department full path. */
export const DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH = 'owningDepartmentFullPath' as const

/** Confluence matrix column: update existing (duplicate) items. */
export const DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS = 'updateExistingDuplicateItems' as const

/** Hunt group membership: device associations. */
export const DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP = 'huntGroupDeviceMembership' as const

/** Hunt group membership: agent associations. */
export const DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP = 'huntGroupAgentMembership' as const

/** Hunt group membership: DNIS associations. */
export const DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS = 'huntGroupDnisAssociations' as const

/**
 * Union of all {@code Device} modal matrix capability keys.
 */
export type DeviceModalCapabilityKey =
    | typeof DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS
    | typeof DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING
    | typeof DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID
    | typeof DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID
    | typeof DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY
    | typeof DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD
    | typeof DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS
    | typeof DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING
    | typeof DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID
    | typeof DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH
    | typeof DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS
    | typeof DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP
    | typeof DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP
    | typeof DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS

/**
 * Readonly capability flags for one Confluence matrix row (device type presentation).
 */
export type DeviceModalCapabilityFlags = Readonly<Record<DeviceModalCapabilityKey, boolean>>
